import type { License } from "../../../../../../store/reseller-admin/licensesStore";
import type { ThemeClasses } from "../types";
import { BILLING_MODELS, BILLING_CYCLES, STATUS_OPTIONS } from "../types";

interface LicenseListRowProps {
  license: License;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (license: License) => void;
  onEdit: (license: License) => void;
  onDelete: (license: License) => void;
}

export default function LicenseListRow({
  license,
  themeClasses,
  isDarkMode,
  onDetails,
  onEdit,
  onDelete,
}: LicenseListRowProps) {
  const statusOpt = STATUS_OPTIONS.find((s) => s.value === license.status);
  const billingModel = BILLING_MODELS.find((b) => b.value === license.billing_model);
  const billingCycle = BILLING_CYCLES.find((c) => c.value === license.billing_cycle);

  const getPrice = () => {
    if (license.billing_model === "per_unit" && license.price_per_unit != null) {
      return `$${license.price_per_unit.toFixed(2)} ${license.currency}/unidad`;
    }
    if (license.billing_model === "flat" && license.flat_price != null) {
      return `$${license.flat_price.toFixed(2)} ${license.currency}`;
    }
    return "Por definir";
  };

  const statusColors: Record<string, string> = {
    active: isDarkMode ? "bg-green-900/40 text-green-400 border-green-700" : "bg-green-50 text-green-700 border-green-200",
    inactive: isDarkMode ? "bg-red-900/40 text-red-400 border-red-700" : "bg-red-50 text-red-700 border-red-200",
    draft: isDarkMode ? "bg-yellow-900/40 text-yellow-400 border-yellow-700" : "bg-yellow-50 text-yellow-700 border-yellow-200",
  };

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 hover:shadow-md transition-all cursor-pointer flex items-center gap-4`}
      onClick={() => onDetails(license)}
    >
      {/* Icon */}
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDarkMode ? "bg-primary-500/20 text-primary-400" : "bg-primary-50 text-primary-600"}`}>
        <i className="ri-key-2-line text-lg"></i>
      </div>

      {/* Name & description */}
      <div className="flex-1 min-w-0">
        <h3 className={`font-semibold text-sm ${themeClasses.text} truncate`}>{license.name}</h3>
        {license.description && (
          <p className={`text-xs ${themeClasses.textSecondary} truncate`}>{license.description}</p>
        )}
      </div>

      {/* Billing model */}
      <div className="hidden md:block text-center min-w-[120px]">
        <p className={`text-xs ${themeClasses.textSecondary}`}>{billingModel?.label}</p>
        <p className={`text-xs ${themeClasses.textSecondary}`}>{billingCycle?.label}</p>
      </div>

      {/* Price */}
      <div className="hidden sm:block text-right min-w-[140px]">
        <p className={`font-bold text-sm ${themeClasses.text}`}>{getPrice()}</p>
      </div>

      {/* Apps count */}
      <div className="hidden lg:block text-center min-w-[80px]">
        <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? "bg-blue-900/30 text-blue-400" : "bg-blue-50 text-blue-700"}`}>
          {license.apps?.length || 0} apps
        </span>
      </div>

      {/* Status */}
      <div className="min-w-[80px] text-center">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs border ${statusColors[license.status] || statusColors.draft}`}>
          {statusOpt?.label || license.status}
        </span>
      </div>

      {/* Actions */}
      <div className="flex gap-1 flex-shrink-0">
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(license); }}
          className={`p-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText} hover:text-blue-500`}
          title="Editar"
        >
          <i className="ri-pencil-line"></i>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(license); }}
          className={`p-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText} hover:text-red-500`}
          title="Desactivar"
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      </div>
    </div>
  );
}
